import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Dropdown } from 'antd';

import useNavbar from '../Navbar/useNavbar';

import { useLanguage } from 'contexts/languageContext';
import { useUser } from 'contexts/userContext';
import useIsMobile from 'hooks/useIsMobile';
import useIsB2BLandingPage from 'hooks/useIsB2BLandingPage';

import { isUserGuide, isB2BUser } from 'utils/isRole';
import { truncateString } from 'utils/truncateString';
import { trackB2BEvent } from 'utils/b2bAnalytics';
import { scrollToId } from 'utils/scrollToId';

import { APP_LANGUAGES } from 'constants/app-languages';
import { FEATURES } from 'constants/feature-flags';
import { AUTH_ROUTES, B2B_ROUTES } from 'constants/routes';

import { OrangeButton } from 'components/Styled/v2/OrangeButton';

import {
  BrazilFlag,
  ContentaLogoMobile,
  ContentaLogoV2,
  LogoutIcon,
  MenuIcon,
  TravelAgencyIcon,
  TravelerIcon,
  TravelExpertIcon,
  USAFlag,
  UserIcon,
} from './icons';

import './styles.scss';

export function NavbarV2({ showDrawer }) {
  const { t } = useTranslation();
  const { logout, user, whoAmI, userRole } = useUser();
  const { currentLanguage, handleChangeLanguage } = useLanguage();
  const isMobile = useIsMobile();
  const { checkURLRedirect } = useNavbar();

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const hasUser = Object.keys(whoAmI).length > 0;

  const isTravelExpert = isUserGuide(userRole);
  const isB2B = isB2BUser(whoAmI) || pathname.startsWith('/b2b/');
  const isB2BLandingPage = useIsB2BLandingPage();
  // Inside a call any navigation skips the leave flow: the guide's chat is
  // not saved and the other side sees a ghost until the SDK times out. The
  // bar keeps the logo and the language switch only.
  const isInCall = pathname.startsWith('/meet/video');

  const handleAnchorClick = (event, id) => {
    event.preventDefault();
    scrollToId(id);
  };

  const handleSelectLanguage = (selectLanguage) => {
    const languageObject = APP_LANGUAGES.find(
      (language) => language.key === selectLanguage
    );
    handleChangeLanguage(languageObject?.key);
  };

  const items = [
    {
      label: (
        <div className="d-flex-row gap-8">
          <USAFlag size={20} />
          {t('common.languages.en-US')}
        </div>
      ),
      key: 'en-US',
    },
    {
      label: (
        <div className="d-flex-row gap-8">
          <BrazilFlag size={20} />
          {t('common.languages.pt-BR')}
        </div>
      ),
      key: 'pt-BR',
    },
  ];

  const userItems = [
    {
      label: (
        <Link className="d-flex-row gap-8" to="/login">
          <TravelerIcon />
          {t('buttons.loginTraveler')}
        </Link>
      ),
      key: 'login-client',
    },
    {
      label: (
        <Link className="d-flex-row gap-8" to="/login/travel-expert">
          <TravelExpertIcon />
          {t('buttons.loginAdvisor')}
        </Link>
      ),
      key: 'login-advisor',
    },

    {
      label: (
        <Link className="d-flex-row gap-8" to="/login/travel-expert">
          <TravelExpertIcon />
          {t('buttons.signUpAsTravelExpert')}
        </Link>
      ),
      key: 'sign-up-advisor',
    },
  ];

  // The B2B landing page's Log In needs three destinations (traveler, travel
  // expert, travel agency) plus the travel-expert signup, spelled out per
  // b2b.landing spec copy instead of the site-wide buttons.* strings.
  const b2bLandingLoginItems = [
    {
      label: (
        <Link className="d-flex-row gap-8" to={AUTH_ROUTES.LOGIN}>
          <TravelerIcon />
          {t('b2b.landing.header.loginAsTraveler')}
        </Link>
      ),
      key: 'b2b-login-traveler',
    },
    {
      label: (
        <Link className="d-flex-row gap-8" to={AUTH_ROUTES.LOGIN_TRAVEL_EXPERT}>
          <TravelExpertIcon />
          {t('b2b.landing.header.loginAsTravelExpert')}
        </Link>
      ),
      key: 'b2b-login-travel-expert',
    },
    {
      label: (
        <Link
          className="d-flex-row gap-8"
          to={B2B_ROUTES.LOGIN}
          onClick={() =>
            trackB2BEvent('b2b_login_click', { section: 'header' })
          }
        >
          <TravelAgencyIcon />
          {t('b2b.landing.header.loginAsTravelAgency')}
        </Link>
      ),
      key: 'b2b-login-agency',
    },
    {
      label: (
        <Link className="d-flex-row gap-8" to={AUTH_ROUTES.GUIDE_REGISTER}>
          <TravelExpertIcon />
          {t('b2b.landing.header.becomeTravelExpert')}
        </Link>
      ),
      key: 'b2b-become-expert',
    },
  ];

  const loggedInItems = [
    {
      label: (
        <Link
          className="d-flex-row gap-8 justify-between"
          onClick={logout}
          to="/logout"
        >
          {t('common.logout')}
          <LogoutIcon />
        </Link>
      ),
      key: 'logout',
    },
  ];

  const renderFlag = () => {
    if (currentLanguage === 'pt-BR' || currentLanguage === 'PT-BR') {
      return <BrazilFlag size={32} />;
    }
    return <USAFlag size={32} />;
  };

  const onClickChangeLanguage = (i) => {
    handleSelectLanguage(i.key);
  };

  return (
    <div
      className={`contenta-header ${isTravelExpert ? 'travel-expert-header' : ''} ${isInCall ? 'contenta-header--call' : ''}`}
    >
      <div className="header__content">
        {isInCall ? (
          <div className="header__content-leftside">
            <span className="header__content-logo-container">
              {isMobile ? <ContentaLogoMobile /> : <ContentaLogoV2 />}
            </span>
          </div>
        ) : (
          <div className="header__content-leftside">
            <button
              className={`trigger header__button${hasUser ? ' header__button--logged' : ''}`}
              onClick={showDrawer}
              type="button"
              aria-label={t('common.menu', { defaultValue: 'Menu' })}
            >
              <MenuIcon />
            </button>
            <Link
              to={checkURLRedirect()}
              className="header__content-logo-container"
              aria-label={t('common.home', { defaultValue: 'Home' })}
            >
              {isMobile ? <ContentaLogoMobile /> : <ContentaLogoV2 />}
            </Link>
          </div>
        )}

        {!isInCall && (
          <nav className="header__nav">
            {isB2BLandingPage ? (
              <>
                <a
                  href="#how-it-works"
                  className="header__nav-link"
                  onClick={(e) => handleAnchorClick(e, 'how-it-works')}
                >
                  {t('b2b.landing.header.howItWorks')}
                </a>
                <a
                  href="#partnership"
                  className="header__nav-link"
                  onClick={(e) => handleAnchorClick(e, 'partnership')}
                >
                  {t('b2b.landing.header.agencyPricing')}
                </a>
                <Link to="/travel-expert" className="header__nav-link">
                  {t('b2b.landing.header.forLocalGuides')}
                </Link>
              </>
            ) : (
              !isB2B && (
                <>
                  <Link to="/#how-it-works" className="header__nav-link">
                    {t('common.nav.howItWorks')}
                  </Link>
                  {FEATURES.b2b && (
                    <Link to="/travelpartners" className="header__nav-link">
                      {t('common.nav.travelAgencies')}
                    </Link>
                  )}
                  <Link to="/travel-expert" className="header__nav-link">
                    {t('common.nav.tourGuides')}
                  </Link>
                </>
              )
            )}
          </nav>
        )}

        <div className="header__content-rightside">
          <Dropdown
            menu={{
              items,
              onClick: onClickChangeLanguage,
            }}
            trigger={['click']}
          >
            <a onClick={(e) => e.preventDefault()}>{renderFlag()}</a>
          </Dropdown>

          {/* The account items are links, not languages: wiring the language
              handler here fed their keys to i18next and blew up on login. */}
          {!isInCall && (
            <Dropdown
              menu={{
                items: hasUser
                  ? loggedInItems
                  : isB2BLandingPage
                    ? b2bLandingLoginItems
                    : userItems,
              }}
              trigger={['click']}
            >
              <a
                className={
                  isB2BLandingPage && !hasUser ? 'header__login-trigger' : ''
                }
                onClick={(e) => e.preventDefault()}
              >
                {!hasUser ? (
                  <>
                    <UserIcon />
                    {isB2BLandingPage && !isMobile && (
                      <span className="header__login-label">
                        {t('b2b.landing.header.logIn')}
                      </span>
                    )}
                  </>
                ) : (
                  <div className="header__content-user-container">
                    <img
                      src={whoAmI?.imageURL || whoAmI?.picture}
                      alt="Avatar"
                      className="header__content-avatar"
                    />
                    {!isMobile && (
                      <div className="header__content-user">
                        <span className="header__content-username">
                          {truncateString(user?.name)}
                        </span>
                        <span className="header__content-email">
                          {truncateString(user?.email)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </a>
            </Dropdown>
          )}

          {isB2BLandingPage && !hasUser && (
            <OrangeButton
              className="header__join-agency-cta"
              onClick={() => {
                trackB2BEvent('b2b_agency_signup_click', {
                  section: 'header',
                });
                navigate(B2B_ROUTES.TRAVEL_PARTNERS_JOIN);
              }}
            >
              {t('b2b.landing.header.joinAgency')}
            </OrangeButton>
          )}
        </div>
      </div>
    </div>
  );
}
